import React, { createContext, useContext, useEffect, useState } from 'react';
import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  updateProfile
} from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db, functions } from '../config/firebase';
import { UserService, RoleService } from '../services/firebaseService';
import { USER_ROLES } from '../models/dataModels';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [userRoles, setUserRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentRole, setCurrentRole] = useState(USER_ROLES.PLAYER);

  // Sign up with email and password
  async function signup(email, password, displayName) {
    try {
      console.log('Creating user with email:', email);
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      console.log('Firebase Auth user created:', userCredential.user.uid);
      
      // Update display name
      await updateProfile(userCredential.user, { displayName });
      console.log('Display name updated');
      
      // Create user document in Firestore
      try {
        await UserService.createUser({
          uid: userCredential.user.uid,
          email: userCredential.user.email,
          displayName: displayName || userCredential.user.email.split('@')[0]
        });
        console.log('User document created in Firestore');
      } catch (firestoreError) {
        console.warn('⚠️ Firestore user creation failed:', firestoreError.message);
        console.log('📝 To create the user document, run:');
        console.log(`   node scripts/create-user-admin.js "${email}" "${displayName}" "${userCredential.user.uid}"`);
      }
      
      return userCredential.user;
    } catch (error) {
      console.error('Signup error details:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      throw error;
    }
  }

  // Sign in with email and password
  async function login(email, password) {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      return userCredential.user;
    } catch (error) {
      throw error;
    }
  }

  // Sign out
  async function logout() {
    try {
      await signOut(auth);
      setUserProfile(null);
      setCurrentRole(USER_ROLES.PLAYER);
    } catch (error) {
      throw error;
    }
  }

  // Reset password via Cloud Function (Resend)
  async function resetPassword(email) {
    try {
      const requestPasswordReset = httpsCallable(functions, 'requestPasswordReset');
      await requestPasswordReset({ email: email.trim() });
    } catch (error) {
      const mapped = new Error(error.message || 'Failed to send password reset email.');
      switch (error.code) {
        case 'functions/not-found':
          mapped.code = 'auth/user-not-found';
          break;
        case 'functions/invalid-argument':
          mapped.code = 'auth/invalid-email';
          break;
        case 'functions/resource-exhausted':
          mapped.code = 'auth/too-many-requests';
          break;
        case 'functions/failed-precondition':
          mapped.code = 'auth/email-not-configured';
          break;
        default:
          mapped.code = error.code || 'auth/unknown';
      }
      throw mapped;
    }
  }

  // Update user profile
  async function updateUserProfile(updates) {
    try {
      if (currentUser) {
        if (Object.keys(updates).length > 0) {
          await UserService.updateUser(currentUser.uid, updates);
        }
        // Refresh user profile
        const updatedProfile = await UserService.getUser(currentUser.uid);
        setUserProfile(updatedProfile);
        
        // Reload role details if profile has roles
        if (updatedProfile && updatedProfile.roles && updatedProfile.roles.length > 0) {
          try {
            const roles = await RoleService.getRolesByIds(updatedProfile.roles);
            setUserRoles(roles);
            
            // Update current role to highest available role
            if (roles.length > 0) {
              const highestRole = roles.reduce((highest, role) => {
                return (role.level || 0) > (highest.level || 0) ? role : highest;
              });
              setCurrentRole(highestRole.id);
            }
          } catch (roleError) {
            console.warn('Could not load role details:', roleError.message);
            // Fallback to role IDs if role details can't be loaded
            setUserRoles(updatedProfile.roles.map(roleId => ({ id: roleId, name: roleId })));
            setCurrentRole(updatedProfile.roles[0] || USER_ROLES.PLAYER);
          }
        } else {
          setUserRoles([]);
          setCurrentRole(USER_ROLES.PLAYER);
        }
      }
    } catch (error) {
      throw error;
    }
  }

  // Change current role
  function changeRole(newRole) {
    if (userProfile && userProfile.roles.includes(newRole)) {
      setCurrentRole(newRole);
    }
  }

  // Get available roles for current user
  function getAvailableRoles() {
    if (!userProfile) return [];
    return userProfile.roles;
  }

  // Check if user has specific role
  function hasRole(role) {
    if (!userProfile) return false;
    return userProfile.roles.includes(role);
  }

  // Check if user has permission for specific role
  function hasPermission(requiredRole) {
    if (!userProfile || !userRoles.length) return false;
    
    // Find the required role details
    const requiredRoleDetails = userRoles.find(role => role.id === requiredRole);
    if (!requiredRoleDetails) return false;
    
    // Get user's highest role level
    const userHighestLevel = Math.max(...userRoles.map(role => role.level || 0));
    const requiredLevel = requiredRoleDetails.level || 0;
    
    return userHighestLevel >= requiredLevel;
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      
      if (user) {
        try {
          // Get user profile from Firestore first
          const profile = await UserService.getUser(user.uid);
          setUserProfile(profile);
          
          // Update last login timestamp only if user document exists (best effort - don't fail if this doesn't work)
          if (profile) {
            try {
              await UserService.updateUser(user.uid, {
                lastLogin: serverTimestamp()
              });
            } catch (updateError) {
              console.warn('Could not update last login timestamp:', updateError.message);
            }
          }
          
          // Load role details for the user
          if (profile && profile.roles && profile.roles.length > 0) {
            try {
              const roles = await RoleService.getRolesByIds(profile.roles);
              setUserRoles(roles);
              
              // Set initial role to highest available role
              if (roles.length > 0) {
                const highestRole = roles.reduce((highest, role) => {
                  return (role.level || 0) > (highest.level || 0) ? role : highest;
                });
                setCurrentRole(highestRole.id);
              }
            } catch (roleError) {
              console.warn('Could not load role details:', roleError.message);
              // Fallback to role IDs if role details can't be loaded
              setUserRoles(profile.roles.map(roleId => ({ id: roleId, name: roleId })));
              setCurrentRole(profile.roles[0] || USER_ROLES.PLAYER);
            }
          } else {
            setUserRoles([]);
            setCurrentRole(USER_ROLES.PLAYER);
          }
        } catch (error) {
          // Only log the error once per session to avoid spam
          if (!sessionStorage.getItem('firestore-error-logged')) {
            console.warn('⚠️ Firestore access blocked by security rules. Using fallback profile.');
            console.log('📝 To create the user profile, run:');
            console.log(`   node scripts/create-user-admin.js "${user.email}" "${user.displayName || user.email.split('@')[0]}" "${user.uid}"`);
            sessionStorage.setItem('firestore-error-logged', 'true');
          }
          
          // Set a default profile so the user can still use the app
          setUserProfile({
            id: user.uid,
            email: user.email,
            displayName: user.displayName || user.email.split('@')[0],
            roles: [USER_ROLES.PLAYER],
            teams: {},
            managedLeagues: []
          });
          setUserRoles([{ id: USER_ROLES.PLAYER, name: 'Player', level: 1 }]);
          setCurrentRole(USER_ROLES.PLAYER);
        }
      } else {
        setUserProfile(null);
        setUserRoles([]);
        setCurrentRole(USER_ROLES.PLAYER);
        // Clear the error flag when user logs out
        sessionStorage.removeItem('firestore-error-logged');
      }
      
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    userProfile,
    userRoles,
    currentRole,
    loading,
    signup,
    login,
    logout,
    resetPassword,
    updateUserProfile,
    changeRole,
    getAvailableRoles,
    hasRole,
    hasPermission
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
